import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {
    addDeliveryFavorite,
    createMetadata,
    getAllCountryOptions,
    getAllDeliveries,
    getAllRegionsOptions,
    getCollectionStatuses,
    getRecentDeliveries,
    removeDeliveryFavorite,
    updateMetadata,
    addCollectionStatusFavorite,
    removeCollectionStatusFavorite,
    getFavoriteDeliveries,
    getFavoriteCollectionStatuses, getUsers, getRefinitivDashboardStats
} from "../utils/Api";
import {TableState, tableStateToParams} from "../components/EnhancedTable/EnhancedTable";
import {useCurrentUser} from "./users";

async function createRegionMap() {
    const regions = await getAllRegionsOptions();
    const regionMap = {};
    regions.forEach(r => {
        regionMap[r.id] = r.name
    });
    return regionMap;
}

function updateRowMetadataCache(oldData, resData) {
    const newResults = oldData['results'].map(currentRow => {
        if (resData.collection_id === currentRow.name) {
            return {
                ...currentRow,
                metadata: resData
            };
        }
        return currentRow;
    });

    return {
        ...(oldData as object),
        results: newResults
    }
}

function toggleFavorite(oldData, name) {
    const newResults = oldData['results'].map(currentRow => {
        if (name === currentRow.name) {
            return {
                ...currentRow,
                favorite: !currentRow['favorite']
            };
        }
        return currentRow;
    });

    return {
        ...(oldData as object),
        results: newResults
    }
}

function removeRow(oldData, name) {
    const newResults = oldData['results'].filter(currentRow => {
        return name !== currentRow.name;
    });

    return {
        ...(oldData as object),
        results: newResults
    }
}

function addRow(oldData, row) {
    return {
        ...(oldData as object),
        results: [row, ...oldData['results']]
    }
}

function updateMetadataRow(row: { name: string, metadata: object }, table: string) {
    const {
        name,
        metadata
    } = row;

    // If the id is missing it means we need to create a new metadata entry.
    const metadataId = metadata['id'] || null;
    if (!metadataId) {
        metadata['collection_id'] = name;
        metadata['table'] = table;
        return createMetadata(metadata);
    }

    return updateMetadata(metadataId, metadata)
}

/**
 * Returns whether the current user belongs to the Refinitiv org.
 */
export const useIsRefinitiv = () => {
    const queryClient = useQueryClient();
    const currentUser = queryClient.getQueryData(["currentUser"]);
    return currentUser['organization'].name.toLowerCase() === 'refinitiv';
};

/**
 * Retrieves the list of possible regions from the API
 * and converts it to a map for easy lookups.
 */
export const useRegionMap = () => {
    return useQuery({
        queryKey: ["regionsMap"],
        refetchOnWindowFocus: false,
        queryFn: async () => {
            return createRegionMap()
        }
    })
};

/**
 * Retrieves the list of possible countries from the API
 * and converts it to a map for easy lookups.
 */
export const useCountryMap = () => {
    return useQuery({
        queryKey: ["countriesMap"],
        refetchOnWindowFocus: false,
        queryFn: async () => {
            const countries = await getAllCountryOptions();
            const countryMap = {};
            countries.forEach(r => {
                countryMap[r.id] = r.name
            });
            return countryMap;
        }
    });
};

/**
 *
 * Collection status hooks
 *
 */

/**
 * Get data for the collection status table.
 */
export const useCollectionStatusTableData = (tableState: TableState) => {
    const queryParams = tableStateToParams(tableState);
    return useQuery(["collectionStatuses", queryParams], async () => {
        return await getCollectionStatuses(queryParams);
    });
}

/**
 * Get data for the favorites collection statuses table.
 */
export const useFavoriteCollectionStatusTableData = (tableState: TableState) => {
    const queryParams = tableStateToParams(tableState);
    return useQuery(["favoriteCollectionStatusTableData", queryParams], async () => {
        return await getFavoriteCollectionStatuses(queryParams);
    });
}


/**
 * Toggle a collection status favorite.
 */
export const useToggleCollectionStatusFavorite = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (
            variables: { row: object, tableState: TableState }
        ) => {
            if (variables.row['favorite']) {
                return removeCollectionStatusFavorite(variables.row['name']);
            }
            return addCollectionStatusFavorite(variables.row['name']);
        },
        onSuccess: (resData, variables) => {
            queryClient.setQueriesData(["collectionStatuses"], (oldData) => {
                return toggleFavorite(oldData, variables.row['name'])
            });

            queryClient.setQueriesData({queryKey: ['favoriteCollectionStatusTableData']}, (oldData) => {
                if (variables.row['favorite']) {
                    return removeRow(oldData, variables.row['name'])
                }
                variables.row['favorite'] = true;
                return addRow(oldData, variables.row)
            });
        },
    });
}

/**
 * Updates a collection status metadata object and updates that row in the query cache.
 */
export const useUpdateCollectionStatusRow = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (
            variables: { row: { name: string, metadata: object }, tableState: TableState }
        ) => {
            return updateMetadataRow(variables.row, "CollectionStatus");
        },
        onSuccess: (resData, variables) => {
            queryClient.setQueriesData(["collectionStatuses"], (oldData) => {
                return updateRowMetadataCache(oldData, resData)
            });

            queryClient.setQueriesData(["favoriteCollectionStatusTableData"], (oldData) => {
                return updateRowMetadataCache(oldData, resData)
            });
        },
    });
}

/**
 *
 * Deliveries hooks
 *
 */

/**
 * Get data for the all deliveries table.
 */
export const useAllDeliveriesTableData = (tableState: TableState) => {
    const queryParams = tableStateToParams(tableState);
    return useQuery(["allDeliveriesTableData", queryParams], async () => {
        return await getAllDeliveries(queryParams);
    });
}

/**
 * Get data for the favorites deliveries table.
 */
export const useFavoriteAllDeliveriesTableData = (tableState: TableState) => {
    const queryParams = tableStateToParams(tableState);
    return useQuery(["favoriteAllDeliveriesTableData", queryParams], async () => {
        return await getFavoriteDeliveries(queryParams);
    });
}


/**
 * Get data for the recent deliveries table.
 */
export const useRecentDeliveriesTableData = (tableState: TableState, favoritesOnly: boolean = false) => {
    return useQuery(["recentDeliveriesTableData", favoritesOnly], getRecentDeliveries, {
        select: (rows) => {
            const {
                page,
                pageSize,
                searchString,
                orderBy,
                order
            } = tableState;

            let filteredRows = [...rows.results];

            if (favoritesOnly) {
                filteredRows = filteredRows.filter(row => row.favorite);
            }

            // First filter rows that match our search string.
            if (searchString) {
                filteredRows = filteredRows.filter(row => {
                    return row['name'].toLowerCase().includes(searchString) ||
                        row['date'].toLowerCase().includes(searchString) ||
                        (row.metadata && row.metadata.region?.name.toLowerCase().includes(searchString)) ||
                        (row.metadata && row.metadata.country?.name.toLowerCase().includes(searchString));

                });

            }

            // Next sort by the provided field.
            filteredRows.sort((a, b) => {
                if (orderBy === "date") {
                     if (order === 'desc') {
                        return Date.parse(b[orderBy]) - Date.parse(a[orderBy])
                    }
                    return Date.parse(a[orderBy]) - Date.parse(b[orderBy])
                } else {
                    if (order === 'desc') {
                        return b[orderBy].toString().localeCompare(a[orderBy].toString())
                    }
                    return a[orderBy].toString().localeCompare(b[orderBy].toString());
                }

            });

            let resultRows = filteredRows
            if (pageSize > -1) {
                const offsetIdx = (page - 1) * pageSize;
                resultRows = filteredRows.slice(offsetIdx, offsetIdx + pageSize);
            }

            return {
                count: filteredRows.length,
                results: resultRows
            }
        }

    });
}

/**
 * Updates a delivery metadata object and updates that row in applicable query caches.
 */
export const useUpdateAllDeliveriesRow = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (
            variables: { row: { name: string, metadata: object }, tableState: TableState }
        ) => {
            return updateMetadataRow(variables.row, "PostHistory")
        },
        onSuccess: (resData, variables) => {
            queryClient.setQueriesData(["allDeliveriesTableData"], (oldData) => {
                return updateRowMetadataCache(oldData, resData)
            });

            queryClient.setQueriesData(["recentDeliveriesTableData"], (oldData) => {
                return updateRowMetadataCache(oldData, resData)
            });

            queryClient.setQueriesData(["favoriteAllDeliveriesTableData"], (oldData) => {
                return updateRowMetadataCache(oldData, resData)
            });
        },
    });
}

/**
 * Updates a delivery metadata object and updates that row in applicable query caches.
 */
export const useUpdateRecentDeliveriesRow = () => {
    const queryClient = useQueryClient();
    return useMutation({
            mutationFn: (
                variables: { row: { name: string, metadata: object }, tableState: TableState }
            ) => {
                return updateMetadataRow(variables.row, "PostHistory");
            },
            onSuccess: async (resData, variables) => {
                queryClient.setQueriesData(["recentDeliveriesTableData"], (oldData) => {
                    return updateRowMetadataCache(oldData, resData)
                });

                queryClient.setQueriesData({queryKey: ['allDeliveriesTableData']}, (oldData) => {
                    return updateRowMetadataCache(oldData, resData)
                });

                queryClient.setQueriesData({queryKey: ['favoriteAllDeliveriesTableData']}, (oldData) => {
                    return updateRowMetadataCache(oldData, resData)
                });
            },
        }
    );
}

/**
 * Toggle favorites for deliveries and collections.
 */
export const useToggleDeliveryFavorite = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (
            variables: { row: object, tableState: TableState }
        ) => {
            if (variables.row['favorite']) {
                return removeDeliveryFavorite(variables.row['name']);
            }
            return addDeliveryFavorite(variables.row['name']);
        },
        onSuccess: (resData, variables) => {
            queryClient.setQueriesData({queryKey: ['recentDeliveriesTableData']}, (oldData) => {
                return toggleFavorite(oldData, variables.row['name'])
            });

            queryClient.setQueriesData({queryKey: ['allDeliveriesTableData']}, (oldData) => {
                return toggleFavorite(oldData, variables.row['name'])
            });

            queryClient.setQueriesData({queryKey: ['favoriteAllDeliveriesTableData']}, (oldData) => {
                if (variables.row['favorite']) {
                    return removeRow(oldData, variables.row['name'])
                }
                variables.row['favorite'] = true;
                return addRow(oldData, variables.row)
            });
        },
    });
}

/**
 * Gets data for the Refinitiv Stats panel.
 */
export const useRefinitivStatsData = () => {
    return useQuery(["refinitivStats"], getRefinitivDashboardStats);
}